// Full updated useTenant.tsx with additional logs

import { useEffect, useState, createContext, useContext } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type Client = Database['public']['Tables']['clients']['Row'];

interface Tenant {
  id: string;
  user_id: string;
  branding: {
    primaryColor?: string;
    secondaryColor?: string;
    logo?: string;
    companyName?: string;
    tagline?: string;
  };
  // Add other relevant fields as needed
}

const TenantContext = createContext<
  | { tenant: Tenant | null; isCustomDomain: boolean; loading: boolean }
  | undefined
>(undefined);

export function TenantProvider({ children }: { children: React.ReactNode }) {
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [isCustomDomain, setIsCustomDomain] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // console.log('[useTenant] useEffect started', {
    //   hostname: window.location.hostname,
    //   fullUrl: window.location.href,
    // });

    const fetchTenant = async () => {
      try {
        const hostname = window.location.hostname;
        // console.log('[useTenant] fetchTenant started', {
        //   hostname,
        //   fullUrl: window.location.href,
        // });

        const isDefault =
          hostname === 'app.callsparker.com' ||
          hostname === 'localhost' ||
          hostname === 'scaling-space-succotash-4vvwq4vrjxrhqxgp-8080.app.github.dev' ||
          hostname === 'dev-callsparker.vercel.app' || 
          hostname === 'cristi-dev-callsparker.vercel.app' ||
          hostname === 'chris-dev-callsparker.vercel.app' ||
          hostname.endsWith('.localhost');

        // console.log('[useTenant] isDefault check result', {
        //   isDefault,
        //   hostname,
        // });

        if (isDefault) {
          // console.log(
          //   '[useTenant] Default domain detected - setting isCustomDomain to false'
          // );
          setIsCustomDomain(false);
          setTenant(null);
          setLoading(false);
          return;
        }

        // console.log(
        //   '[useTenant] Non-default domain - setting isCustomDomain to true'
        // );
        setIsCustomDomain(true);

        // Split for subdomain check
        const subdomainPrefix = hostname.split('.')[0];
        // console.log('[useTenant] Subdomain prefix', {
        //   subdomainPrefix,
        //   hostname,
        // });

        const { data, error } = await supabase
          .rpc('get_business_entry', {
            p_hostname: hostname,
            p_subdomain_prefix: subdomainPrefix,
          })
          .single();

        // console.log('[useTenant] RPC query executed', {
        //   data: data ? JSON.stringify(data, null, 2) : 'null',
        //   error: error ? JSON.stringify(error, null, 2) : 'null',
        //   hostname,
        //   subdomainPrefix,
        // });

        if (error) {
          console.error('[useTenant] RPC error fetching tenant:', error);
          // console.log(
          //   '[useTenant] Error occurred - keeping isCustomDomain true, no tenant'
          // );
          setTenant(null);
          return;
        }

        if (data) {
          // console.log('[useTenant] Custom domain matched with data', {
          //   data: JSON.stringify(data, null, 2),
          // });
          setTenant({
            id: (data as any).id,
            user_id: (data as any).user_id,
            branding: (data as any).branding as Tenant['branding'],
          });
        } else {
          // console.log(
          //   '[useTenant] No matching custom domain data - keeping isCustomDomain true, no tenant'
          // );
          setTenant(null);
        }
      } catch (err) {
        console.error('[useTenant] Unexpected error:', err);
        // console.log(
        //   '[useTenant] Unexpected error - keeping isCustomDomain true, no tenant'
        // );
        setTenant(null);
      } finally {
        // console.log('[useTenant] Finally block - setting loading to false', {
        //   isCustomDomain,
        //   tenant: tenant ? JSON.stringify(tenant, null, 2) : 'null',
        //   fullUrl: window.location.href,
        // });
        setLoading(false);
      }
    };

    fetchTenant();
  }, []);

  if (loading) {
    // console.log('[useTenant] TenantProvider returning loading state', {
    //   fullUrl: window.location.href,
    // });
    return <div>Loading...</div>; // Or a spinner component
  }

  // console.log('[useTenant] TenantProvider rendering children', {
  //   isCustomDomain,
  //   tenant: tenant ? JSON.stringify(tenant, null, 2) : 'null',
  //   fullUrl: window.location.href,
  // });
  return (
    <TenantContext.Provider value={{ tenant, isCustomDomain, loading }}>
      {children}
    </TenantContext.Provider>
  );
}

export function useTenant() {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
